import React, { useState, useMemo, useEffect, useRef } from 'react';
import { usePartnersData, Partner } from '../contexts/PartnersDataProvider';

interface PartnerSelectorProps {
  onSelect: (membershipType: number, partnerName: string) => void;
  onCancel?: () => void;
  buttonText?: string;
  className?: string;
}

const PartnerSelector: React.FC<PartnerSelectorProps> = ({
  onSelect,
  onCancel,
  buttonText = 'Select Partner',
  className = '',
}) => {
  const { partners, loading, error, ensureLoaded } = usePartnersData();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  // Ensure partners are loaded when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      void ensureLoaded();
    }
  }, [showDropdown, ensureLoaded]);

  // Filter partners based on search query
  const filteredPartners = useMemo(() => {
    if (!searchQuery.trim()) return partners;
    const query = searchQuery.toLowerCase();
    return partners.filter(p =>
      p.name.toLowerCase().includes(query) ||
      p.code.toLowerCase().includes(query)
    );
  }, [partners, searchQuery]);

  // Auto-focus hidden input when dropdown opens
  useEffect(() => {
    if (showDropdown && hiddenInputRef.current) {
      hiddenInputRef.current.focus();
    }
  }, [showDropdown]);

  const handlePartnerSelect = (partner: Partner) => {
    onSelect(partner.membership_type, partner.name);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleCancel = () => {
    setShowDropdown(false);
    setSearchQuery('');
    onCancel?.();
  };

  // Handle keyboard input for search
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (e.key === 'Enter' && filteredPartners.length > 0) {
      e.preventDefault();
      handlePartnerSelect(filteredPartners[0]);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Reset search when the dropdown is being toggled closed
          // (other close paths reset via handleCancel/handlePartnerSelect)
          if (showDropdown) {
            setSearchQuery('');
          }
          setShowDropdown(!showDropdown);
        }}
        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed w-full"
        disabled={loading}
      >
        {loading ? 'Loading...' : buttonText}
      </button>

      {showDropdown && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={handleCancel}
          />

          {/* Hidden input for keyboard capture */}
          <input
            ref={hiddenInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="absolute opacity-0 pointer-events-none w-px h-px"
            aria-hidden="true"
          />

          {/* Dropdown */}
          <div className="absolute z-50 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-y-auto">
            {/* Search query indicator */}
            {searchQuery && (
              <div className="sticky top-0 px-4 py-2 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-700 text-sm">
                <span className="text-purple-600 dark:text-purple-400 font-semibold">
                  Filtering: {searchQuery}
                </span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSearchQuery('');
                    hiddenInputRef.current?.focus();
                  }}
                  className="ml-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
                >
                  ✕
                </button>
              </div>
            )}

            {error && (
              <div className="p-4 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            {!error && partners.length === 0 && !loading && (
              <div className="p-4 text-gray-500 dark:text-gray-400 text-sm">
                No partners available
              </div>
            )}

            {!error && filteredPartners.length === 0 && partners.length > 0 && (
              <div className="p-4 text-gray-500 dark:text-gray-400 text-sm">
                No partners match &ldquo;{searchQuery}&rdquo;
              </div>
            )}

            {filteredPartners.map((partner) => (
              <button
                key={partner.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePartnerSelect(partner);
                }}
                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
              >
                <div className="font-semibold text-gray-900 dark:text-gray-100">
                  {partner.name}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Code: {partner.code}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PartnerSelector;
